/**
 * CUSTOMER-172B — focus → first-character timing + long-task observation.
 * Non-PII only. Enabled when ?search_perf=1 or window.__AS_SEARCH_PERF_DIAG__=1.
 */
export type FocusInputTraceEvent = {
  name: string;
  t: number;
  /** Optional numeric extras only — never query text. */
  extra?: Record<string, number>;
};

type TraceState = {
  active: boolean;
  events: FocusInputTraceEvent[];
  longTasks: Array<{ start: number; duration: number }>;
  observer: PerformanceObserver | null;
  firstPaintLogged: boolean;
};

const state: TraceState = {
  active: false,
  events: [],
  longTasks: [],
  observer: null,
  firstPaintLogged: false,
};

export function isSearchPerfDiagEnabled(): boolean {
  if (typeof window === "undefined") return false;
  const w = window as Window & { __AS_SEARCH_PERF_DIAG__?: boolean };
  if (w.__AS_SEARCH_PERF_DIAG__ === true) return true;
  try {
    return new URLSearchParams(window.location.search).get("search_perf") === "1";
  } catch {
    return false;
  }
}

function push(name: string, extra?: Record<string, number>): void {
  if (!state.active && !isSearchPerfDiagEnabled()) return;
  state.active = true;
  const t = performance.now();
  if (state.events.length < 400) {
    state.events.push(extra ? { name, t, extra } : { name, t });
  }
  try {
    performance.mark?.(`as_ftfi_${name}`);
  } catch {
    /* ignore */
  }
  const w = window as Window & { __AS_FOCUS_INPUT_TRACE__?: FocusInputTraceEvent[] };
  w.__AS_FOCUS_INPUT_TRACE__ = state.events;
}

export function startFocusInputTrace(): void {
  if (!isSearchPerfDiagEnabled()) return;
  state.active = true;
  state.events = [];
  state.longTasks = [];
  state.firstPaintLogged = false;
  push("trace_start");
  try {
    if (typeof PerformanceObserver !== "undefined" && !state.observer) {
      const obs = new PerformanceObserver((list) => {
        for (const e of list.getEntries()) {
          if (e.duration >= 50) {
            state.longTasks.push({ start: e.startTime, duration: e.duration });
          }
        }
        const w = window as Window & {
          __AS_LONG_TASKS__?: Array<{ start: number; duration: number }>;
        };
        w.__AS_LONG_TASKS__ = state.longTasks.slice(-40);
      });
      obs.observe({ type: "longtask", buffered: true } as PerformanceObserverInit);
      state.observer = obs;
    }
  } catch {
    /* longtask not supported */
  }
}

export function traceFocusInput(name: string, extra?: Record<string, number>): void {
  push(name, extra);
}

export function traceFirstCharPaint(): void {
  if (state.firstPaintLogged) return;
  state.firstPaintLogged = true;
  push("first_char_paint");
}

export function getFocusInputTraceSummary(): {
  focus_to_input_ms: number | null;
  first_key_to_paint_ms: number | null;
  long_task_count: number;
  max_long_task_ms: number;
  events: FocusInputTraceEvent[];
} {
  const focus = state.events.find((e) => e.name === "focus");
  const beforeInput = state.events.find((e) => e.name === "beforeinput");
  const input = state.events.find((e) => e.name === "input");
  const paint = state.events.find((e) => e.name === "first_char_paint");
  const keyStart = beforeInput?.t ?? input?.t ?? null;
  return {
    focus_to_input_ms:
      focus && keyStart != null ? Math.max(0, keyStart - focus.t) : null,
    first_key_to_paint_ms:
      keyStart != null && paint ? Math.max(0, paint.t - keyStart) : null,
    long_task_count: state.longTasks.length,
    max_long_task_ms: state.longTasks.reduce((m, x) => Math.max(m, x.duration), 0),
    events: [...state.events],
  };
}

export function stopFocusInputTrace(): void {
  state.active = false;
  try {
    state.observer?.disconnect();
  } catch {
    /* ignore */
  }
  state.observer = null;
}
